import { useEffect, useState } from "react";
import { Plus, Save, Trash2, ArrowUp, ArrowDown, Eye, EyeOff, X, Award } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";

const EMPTY = {
    full_name: "",
    title: "",
    bio: "",
    photo_url: "",
    linkedin_url: "",
    affiliations: [],
    order: 0,
    visible: true,
    bio_pending: false,
    temoignage_pa: "",
    temoignage_pending: true,
};

export default function AdminComiteScientifique() {
    const [members, setMembers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [editing, setEditing] = useState(null);
    const [saving, setSaving] = useState(false);
    const [affilInput, setAffilInput] = useState("");
    const [expertiseInput, setExpertiseInput] = useState("");
    const [pubInput, setPubInput] = useState("");

    useEffect(() => {
        document.title = "Admin · Comité scientifique";
        reload();
    }, []);

    const reload = async () => {
        setLoading(true);
        try {
            const { data } = await api.get("/admin/comite-scientifique");
            setMembers(data || []);
        } catch (err) {
            toast.error(formatApiError(err));
        } finally {
            setLoading(false);
        }
    };

    const startNew = () =>
        setEditing({ ...EMPTY, order: (members.at(-1)?.order || 0) + 1, _new: true });
    const startEdit = (m) => setEditing({ ...m });
    const cancelEdit = () => {
        setEditing(null);
        setAffilInput("");
    };

    const addAffil = () => {
        const v = affilInput.trim();
        if (!v) return;
        if (editing.affiliations.includes(v)) return;
        setEditing({ ...editing, affiliations: [...editing.affiliations, v] });
        setAffilInput("");
    };

    const removeAffil = (v) =>
        setEditing({ ...editing, affiliations: editing.affiliations.filter((x) => x !== v) });

    const addToList = (field, value, setter) => {
        const v = value.trim();
        if (!v) return;
        const list = editing[field] || [];
        if (list.includes(v)) return;
        setEditing({ ...editing, [field]: [...list, v] });
        setter("");
    };
    const removeFromList = (field, v) =>
        setEditing({ ...editing, [field]: (editing[field] || []).filter((x) => x !== v) });

    const save = async () => {
        if (!editing.full_name?.trim() || !editing.title?.trim()) {
            toast.error("Nom + titre obligatoires.");
            return;
        }
        setSaving(true);
        try {
            const payload = {
                full_name: editing.full_name,
                title: editing.title,
                bio: editing.bio || "",
                photo_url: editing.photo_url || "",
                linkedin_url: editing.linkedin_url || "",
                affiliations: editing.affiliations || [],
                expertises: editing.expertises || [],
                order: editing.order || 0,
                visible: !!editing.visible,
                bio_pending: !!editing.bio_pending,
                temoignage_pa: editing.temoignage_pa || null,
                temoignage_pending: editing.temoignage_pending == null ? true : !!editing.temoignage_pending,
                email_admin: editing.email_admin || "",
                telephone_admin: editing.telephone_admin || "",
                adresse_admin: editing.adresse_admin || "",
                publications_admin: editing.publications_admin || [],
            };
            if (editing._new) {
                await api.post("/admin/comite-scientifique", payload);
                toast.success("Membre ajouté.");
            } else {
                await api.put(`/admin/comite-scientifique/${editing.id}`, payload);
                toast.success("Membre mis à jour.");
            }
            cancelEdit();
            reload();
        } catch (err) {
            toast.error(formatApiError(err));
        } finally {
            setSaving(false);
        }
    };

    const remove = async (id) => {
        if (!window.confirm("Supprimer définitivement ce membre du comité ?")) return;
        try {
            await api.delete(`/admin/comite-scientifique/${id}`);
            toast.success("Membre supprimé.");
            reload();
        } catch (err) {
            toast.error(formatApiError(err));
        }
    };

    const toggleVisible = async (m) => {
        try {
            await api.put(`/admin/comite-scientifique/${m.id}`, {
                full_name: m.full_name,
                title: m.title,
                bio: m.bio,
                photo_url: m.photo_url,
                linkedin_url: m.linkedin_url,
                affiliations: m.affiliations,
                expertises: m.expertises || [],
                order: m.order,
                visible: !m.visible,
                bio_pending: m.bio_pending,
                temoignage_pa: m.temoignage_pa || null,
                temoignage_pending: m.temoignage_pending == null ? true : !!m.temoignage_pending,
                email_admin: m.email_admin || "",
                telephone_admin: m.telephone_admin || "",
                adresse_admin: m.adresse_admin || "",
                publications_admin: m.publications_admin || [],
            });
            reload();
        } catch (err) {
            toast.error(formatApiError(err));
        }
    };

    const reorder = async (id, direction) => {
        try {
            await api.post(`/admin/comite-scientifique/${id}/reorder?direction=${direction}`);
            reload();
        } catch (err) {
            toast.error(formatApiError(err));
        }
    };

    return (
        <div className="space-y-6" data-testid="admin-comite-page">
            <div className="flex items-center justify-between">
                <div>
                    <h1 className="font-display text-3xl text-brand-ink">Comité scientifique</h1>
                    <p className="mt-1 text-sm text-brand-smoke">
                        Gestion des membres affichés sur <code>/notre-comite-scientifique</code>.
                    </p>
                </div>
                <button
                    type="button"
                    onClick={startNew}
                    data-testid="comite-new-btn"
                    className="inline-flex items-center gap-2 rounded-full bg-brand-orange px-5 py-2.5 text-sm font-semibold text-white hover:bg-[#d9772c]"
                >
                    <Plus className="h-4 w-4" /> Ajouter un membre
                </button>
            </div>

            {loading ? (
                <div className="py-12 text-center text-sm text-brand-smoke">Chargement…</div>
            ) : (
                <div className="space-y-3" data-testid="comite-list">
                    {members.length === 0 && (
                        <div className="rounded-xl border border-dashed border-brand-line bg-[#FAFAFA] py-12 text-center text-sm text-brand-smoke">
                            Aucun membre pour le moment.
                        </div>
                    )}
                    {members.map((m, i) => (
                        <div
                            key={m.id}
                            className="flex flex-wrap items-center gap-4 rounded-xl border border-brand-line bg-white p-4"
                            data-testid={`comite-row-${i}`}
                        >
                            <div className="flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-full bg-brand-orange/10 font-display text-sm text-brand-orange">
                                {m.full_name?.split(" ").filter(Boolean).slice(0, 2).map((w) => w[0]).join("")}
                            </div>
                            <div className="min-w-0 flex-1">
                                <div className="flex items-center gap-2">
                                    <strong className="text-brand-ink">{m.full_name}</strong>
                                    {!m.visible && (
                                        <span className="rounded-full bg-brand-smoke/20 px-2 py-0.5 text-[10px] uppercase text-brand-smoke">Masqué</span>
                                    )}
                                    {m.bio_pending && (
                                        <span className="rounded-full bg-brand-orange/15 px-2 py-0.5 text-[10px] uppercase text-brand-orange">Bio en attente</span>
                                    )}
                                </div>
                                <p className="truncate text-xs text-brand-smoke">{m.title}</p>
                            </div>
                            <div className="flex gap-1.5">
                                <button onClick={() => reorder(m.id, "up")} className="rounded-full border border-brand-line p-2 hover:border-brand-ink" aria-label="Monter">
                                    <ArrowUp className="h-3.5 w-3.5" />
                                </button>
                                <button onClick={() => reorder(m.id, "down")} className="rounded-full border border-brand-line p-2 hover:border-brand-ink" aria-label="Descendre">
                                    <ArrowDown className="h-3.5 w-3.5" />
                                </button>
                                <button onClick={() => toggleVisible(m)} className="rounded-full border border-brand-line p-2 hover:border-brand-ink" aria-label="Visibilité">
                                    {m.visible ? <Eye className="h-3.5 w-3.5" /> : <EyeOff className="h-3.5 w-3.5" />}
                                </button>
                                <button onClick={() => startEdit(m)} data-testid={`comite-edit-${i}`} className="rounded-full bg-brand-ink px-4 py-2 text-xs font-semibold text-white hover:bg-brand-coal">
                                    Éditer
                                </button>
                                <button onClick={() => remove(m.id)} className="rounded-full border border-red-200 p-2 text-red-600 hover:bg-red-50" aria-label="Supprimer">
                                    <Trash2 className="h-3.5 w-3.5" />
                                </button>
                            </div>
                        </div>
                    ))}
                </div>
            )}

            {/* Edit drawer */}
            {editing && (
                <div className="fixed inset-0 z-50 flex items-end justify-end bg-black/40 backdrop-blur-sm" onClick={cancelEdit}>
                    <div className="h-full w-full max-w-xl overflow-y-auto bg-white p-8 shadow-2xl" onClick={(e) => e.stopPropagation()} data-testid="comite-edit-drawer">
                        <div className="flex items-center justify-between">
                            <h2 className="font-display text-2xl text-brand-ink">
                                {editing._new ? "Nouveau membre" : "Éditer le membre"}
                            </h2>
                            <button onClick={cancelEdit} className="rounded-full p-2 hover:bg-brand-line/40" aria-label="Fermer">
                                <X className="h-5 w-5" />
                            </button>
                        </div>
                        <div className="mt-6 space-y-4">
                            <Field label="Nom complet *">
                                <input value={editing.full_name} onChange={(e) => setEditing({ ...editing, full_name: e.target.value })} data-testid="comite-input-name" className="input" />
                            </Field>
                            <Field label="Titre / spécialité *">
                                <input value={editing.title} onChange={(e) => setEditing({ ...editing, title: e.target.value })} placeholder="Ex: PU-PH, Médecine du Sport, CHU Lyon" data-testid="comite-input-title" className="input" />
                            </Field>
                            <Field label="Bio (4-5 lignes)">
                                <textarea rows={5} value={editing.bio} onChange={(e) => setEditing({ ...editing, bio: e.target.value })} data-testid="comite-input-bio" className="input" />
                            </Field>
                            <Field label="Témoignage Prévention Active (2-4 phrases)">
                                <textarea
                                    rows={5}
                                    value={editing.temoignage_pa || ""}
                                    onChange={(e) => setEditing({ ...editing, temoignage_pa: e.target.value })}
                                    placeholder="Regard socio-économique + vertus de la Prévention Active pour les thérapeutes…"
                                    data-testid="comite-input-temoignage"
                                    className="input"
                                />
                                <label className="mt-2 flex items-center gap-2 text-xs text-brand-smoke">
                                    <input
                                        type="checkbox"
                                        checked={editing.temoignage_pending == null ? true : !!editing.temoignage_pending}
                                        onChange={(e) => setEditing({ ...editing, temoignage_pending: e.target.checked })}
                                        className="h-4 w-4 accent-brand-orange"
                                        data-testid="comite-input-temoignage-pending"
                                    />
                                    Marquer comme « en cours de validation »
                                </label>
                            </Field>
                            <Field label="Photo (URL)">
                                <input value={editing.photo_url} onChange={(e) => setEditing({ ...editing, photo_url: e.target.value })} placeholder="https://…" className="input" />
                            </Field>
                            <Field label="LinkedIn (URL)">
                                <input value={editing.linkedin_url} onChange={(e) => setEditing({ ...editing, linkedin_url: e.target.value })} placeholder="https://linkedin.com/in/…" className="input" />
                            </Field>
                            <Field label="Affiliations">
                                <div className="flex gap-2">
                                    <input value={affilInput} onChange={(e) => setAffilInput(e.target.value)} onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), addAffil())} placeholder="Ex: AP-HP Pompidou" className="input flex-1" />
                                    <button type="button" onClick={addAffil} className="rounded-xl bg-brand-ink px-4 py-2.5 text-sm font-semibold text-white">+</button>
                                </div>
                                {editing.affiliations?.length > 0 && (
                                    <div className="mt-2 flex flex-wrap gap-1.5">
                                        {editing.affiliations.map((a) => (
                                            <span key={a} className="inline-flex items-center gap-1 rounded-full border border-brand-orange/30 bg-brand-orange/5 px-2.5 py-1 text-xs">
                                                <Award className="h-3 w-3 text-brand-orange" /> {a}
                                                <button onClick={() => removeAffil(a)} className="ml-1 hover:text-red-600">×</button>
                                            </span>
                                        ))}
                                    </div>
                                )}
                            </Field>
                            <Field label="Expertises / Tags">
                                <div className="flex gap-2">
                                    <input value={expertiseInput} onChange={(e) => setExpertiseInput(e.target.value)} onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), addToList("expertises", expertiseInput, setExpertiseInput))} placeholder="Ex: Thérapie Manuelle Avancée" className="input flex-1" data-testid="comite-input-expertise" />
                                    <button type="button" onClick={() => addToList("expertises", expertiseInput, setExpertiseInput)} className="rounded-xl bg-brand-ink px-4 py-2.5 text-sm font-semibold text-white">+</button>
                                </div>
                                {editing.expertises?.length > 0 && (
                                    <div className="mt-2 flex flex-wrap gap-1.5">
                                        {editing.expertises.map((e) => (
                                            <span key={e} className="inline-flex items-center gap-1 rounded-full border border-brand-ink/15 bg-white px-2.5 py-1 text-xs">
                                                {e}
                                                <button onClick={() => removeFromList("expertises", e)} className="ml-1 hover:text-red-600">×</button>
                                            </span>
                                        ))}
                                    </div>
                                )}
                            </Field>

                            <div className="mt-6 rounded-xl border border-dashed border-brand-line bg-[#FAFAFA] p-4">
                                <p className="text-[10px] font-semibold uppercase tracking-wider text-brand-orange">🔒 Informations admin (non publiques)</p>
                                <p className="mt-1 text-[11px] text-brand-smoke">Ces champs ne sont JAMAIS exposés sur le site public — uniquement visibles dans cette interface.</p>
                                <div className="mt-3 space-y-3">
                                    <Field label="Email personnel">
                                        <input value={editing.email_admin || ""} onChange={(e) => setEditing({ ...editing, email_admin: e.target.value })} placeholder="exemple@email.com" className="input" data-testid="comite-input-email-admin" />
                                    </Field>
                                    <Field label="Téléphone">
                                        <input value={editing.telephone_admin || ""} onChange={(e) => setEditing({ ...editing, telephone_admin: e.target.value })} placeholder="06 XX XX XX XX" className="input" />
                                    </Field>
                                    <Field label="Adresse postale">
                                        <input value={editing.adresse_admin || ""} onChange={(e) => setEditing({ ...editing, adresse_admin: e.target.value })} placeholder="Adresse complète" className="input" />
                                    </Field>
                                    <Field label="Publications / Travaux">
                                        <div className="flex gap-2">
                                            <input value={pubInput} onChange={(e) => setPubInput(e.target.value)} onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), addToList("publications_admin", pubInput, setPubInput))} placeholder="Ex: Sport et Arthrose, KS 2013" className="input flex-1" />
                                            <button type="button" onClick={() => addToList("publications_admin", pubInput, setPubInput)} className="rounded-xl bg-brand-ink px-4 py-2.5 text-sm font-semibold text-white">+</button>
                                        </div>
                                        {editing.publications_admin?.length > 0 && (
                                            <ul className="mt-2 space-y-1 text-xs text-brand-ink/80">
                                                {editing.publications_admin.map((p) => (
                                                    <li key={p} className="flex items-start gap-2">
                                                        <span className="mt-1 h-1 w-1 flex-shrink-0 rounded-full bg-brand-orange" />
                                                        <span className="flex-1">{p}</span>
                                                        <button onClick={() => removeFromList("publications_admin", p)} className="text-red-600 hover:underline">×</button>
                                                    </li>
                                                ))}
                                            </ul>
                                        )}
                                    </Field>
                                </div>
                            </div>
                            <div className="flex gap-4">
                                <label className="flex items-center gap-2 text-sm">
                                    <input type="checkbox" checked={!!editing.visible} onChange={(e) => setEditing({ ...editing, visible: e.target.checked })} className="h-4 w-4 accent-brand-orange" />
                                    Visible sur le site
                                </label>
                                <label className="flex items-center gap-2 text-sm">
                                    <input type="checkbox" checked={!!editing.bio_pending} onChange={(e) => setEditing({ ...editing, bio_pending: e.target.checked })} className="h-4 w-4 accent-brand-orange" />
                                    Bio en cours de validation
                                </label>
                            </div>
                            <div className="flex gap-3 pt-4">
                                <button onClick={save} disabled={saving} data-testid="comite-save-btn" className="inline-flex flex-1 items-center justify-center gap-2 rounded-full bg-brand-orange px-5 py-3 text-sm font-semibold text-white hover:bg-[#d9772c] disabled:opacity-60">
                                    <Save className="h-4 w-4" /> {saving ? "Enregistrement…" : "Enregistrer"}
                                </button>
                                <button onClick={cancelEdit} className="rounded-full border border-brand-line px-5 py-3 text-sm font-semibold">Annuler</button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            <style>{`.input { width:100%; border-radius:0.75rem; border:1px solid var(--brand-line, #E5E5E5); padding:0.625rem 1rem; font-size:0.875rem; outline:none; } .input:focus { border-color:#E8873C; box-shadow:0 0 0 3px rgba(232,135,60,0.2); }`}</style>
        </div>
    );
}

function Field({ label, children }) {
    return (
        <div>
            <label className="text-xs font-semibold uppercase tracking-wider text-brand-smoke">{label}</label>
            <div className="mt-1.5">{children}</div>
        </div>
    );
}
